import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import React, { useContext, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  BookTemplate,
  BarChart3,
  TrendingUp,
  Package,
  Loader2,
  FileText,
  Search,
  X,
} from "lucide-react";
import axios from "@/services/axios";
import { useNavigate, useParams } from "react-router-dom";
import AuthContext from "@/store/contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import {
  fetchPublishedTemplates,
  fetchTemplateCategories,
  countBlocksByType,
  type OdashTemplate,
  type TemplateCategory,
} from "@/services/templatesApi";
import { Input } from "@/components/ui/input";

// Icon mapping helper (same as BlockTemplateDialog)
const getIconForCategory = (iconName: string) => {
  const iconMap: { [key: string]: React.ReactNode } = {
    "📊": <BarChart3 className="w-8 h-8" />,
    "💰": <Package className="w-8 h-8" />,
    "👥": <TrendingUp className="w-8 h-8" />,
    "🏭": <Package className="w-8 h-8" />,
    "📋": <FileText className="w-8 h-8" />,
    "📦": <Package className="w-8 h-8" />,
    "🎧": <TrendingUp className="w-8 h-8" />,
    "🎯": <TrendingUp className="w-8 h-8" />,
  };
  return iconMap[iconName] || <BookTemplate className="w-8 h-8" />;
};

const getCategoryColor = (index: number) => {
  const colors = [
    "bg-green-100 text-green-700",
    "bg-blue-100 text-blue-700",
    "bg-orange-100 text-orange-700",
    "bg-purple-100 text-purple-700",
    "bg-indigo-100 text-indigo-700",
    "bg-pink-100 text-pink-700",
    "bg-yellow-100 text-yellow-700",
    "bg-red-100 text-red-700",
  ];
  return colors[index % colors.length];
};

const PageTemplateDialog = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const { page_id } = useParams<{ page_id: string }>();
  const { loadPages, loadConfigs } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<OdashTemplate[]>([]);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [pageTemplateCategories, setPageTemplateCategories] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Load templates when dialog opens and reset search when closed
  useEffect(() => {
    if (open) {
      loadTemplatesData();
    } else {
      setSearchQuery("");
      setSelectedTemplate(null);
    }
  }, [open]);

  const loadTemplatesData = async () => {
    setLoading(true);
    try {
      const [templatesData, categoriesData] = await Promise.all([
        fetchPublishedTemplates(),
        fetchTemplateCategories(),
      ]);

      // Filter for page and dashboard_set templates only
      const pageTemplates = templatesData.filter(
        (template) =>
          template.template_type === "page" ||
          template.template_type === "dashboard_set"
      );

      setTemplates(pageTemplates);
      setCategories(categoriesData);

      // Transform data into category-grouped structure
      const transformedCategories = categoriesData.map((category, index) => {
        // Get templates for this category
        const categoryTemplates = pageTemplates.filter(
          (template) => template.category && template.category.id === category.id
        );

        return {
          id: category.id.toString(),
          categoryName: category.name,
          categoryDescription: category.description,
          icon: getIconForCategory(category.icon),
          color: getCategoryColor(index),
          templates: categoryTemplates,
        };
      }).filter((cat) => cat.templates.length > 0); // Only include categories with templates

      setPageTemplateCategories(transformedCategories);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId: number) => {
    setSelectedTemplate(templateId);
  };

  // Filter categories and templates based on search query (same as BlockTemplateDialog)
  const filterCategoriesAndTemplates = (categories: any[], query: string) => {
    if (!query.trim()) {
      return categories;
    }

    const lowerQuery = query.toLowerCase();

    return categories
      .map((category) => {
        // Check if category name matches
        const categoryMatches = category.categoryName.toLowerCase().includes(lowerQuery);

        // Filter templates within this category
        const filteredTemplates = category.templates.filter((template: OdashTemplate) => {
          // If category matches, include all templates in this category
          if (categoryMatches) {
            return true;
          }

          // Otherwise, check if template matches
          // Search in template name
          if (template.name.toLowerCase().includes(lowerQuery)) {
            return true;
          }

          // Search in short description
          if (template.short_description?.toLowerCase().includes(lowerQuery)) {
            return true;
          }

          // Search in full description (strip HTML tags for better search)
          if (template.description) {
            const strippedDescription = template.description.replace(/<[^>]*>/g, '').toLowerCase();
            if (strippedDescription.includes(lowerQuery)) {
              return true;
            }
          }

          // Search in tags
          if (template.tags?.some(tag => tag.name.toLowerCase().includes(lowerQuery))) {
            return true;
          }

          return false;
        });

        return {
          ...category,
          templates: filteredTemplates,
        };
      })
      .filter((category) => category.templates.length > 0); // Hide empty categories
  };

  const filteredCategories = filterCategoriesAndTemplates(pageTemplateCategories, searchQuery);

  const handleUseTemplate = async () => {
    if (selectedTemplate) {
      const template = templates.find((t) => t.id === selectedTemplate);
      
      if (!template || !template.template_data || !template.template_data.configs) {
        console.error("[PageTemplate] Template or template_data missing", template);
        return;
      }

      // Clone the template data
      let configStr = JSON.stringify(template.template_data);

      // Replace all IDs with new UUIDs to avoid conflicts
      template.template_data.configs.forEach((config: any) => {
        const oldId = config.config?.id;
        if (oldId) {
          const newId = `${oldId.split("-")[0]}-${uuidv4()}`;
          configStr = configStr.replace(new RegExp(oldId, "g"), newId);
        }
      });

      const parsedData: any = JSON.parse(configStr);

      // Get all page configs and component configs
      const pageConfigsData = parsedData.configs.filter((c: any) => c.is_page_config);
      const componentConfigs = parsedData.configs.filter((c: any) => !c.is_page_config);
      
      if (pageConfigsData.length === 0) {
        console.error("[PageTemplate] No page config found in template");
        return;
      }

      // Handle based on template type
      if (template.template_type === "dashboard_set") {
        // Dashboard Set: Replace first page, create others
        let firstPageId = page_id;

        for (let i = 0; i < pageConfigsData.length; i++) {
          const pageConfigData = pageConfigsData[i];
          
          if (i === 0) {
            // Replace the existing page with the first page from template
            const payload = {
              name: pageConfigData.name || "Unnamed Page",
              config: {
                config: pageConfigData.config
              },
              configs: componentConfigs
            };

            await axios
              .post(`/odash/pages/${page_id}/configure`, payload)
              .catch((error) => {
                console.error("[PageTemplate] Error configuring first page:", error);
              });
          } else {
            // Create new pages for additional pages
            // The POST /odash/pages expects the config object directly, not wrapped
            const newPageConfig = { ...pageConfigData.config };
            
            // Set the page title at root level (used by odash.config model for name)
            if (pageConfigData.name) {
              newPageConfig.title = pageConfigData.name;
            }

            await axios
              .post(`/odash/pages`, newPageConfig)
              .catch((error) => {
                console.error("[PageTemplate] Error creating page:", pageConfigData.name, error);
              });
          }
        }

        // Components are already created when configuring the first page
        await loadPages();
        await loadConfigs();
        setTimeout(() => {
          navigate(`/${firstPageId}`);
          setOpen(false);
        }, 100);

      } else {
        // Single Page: Configure existing page
        const pageConfigData = pageConfigsData[0];

        // Build the payload expected by backend
        const payload = {
          name: pageConfigData.name || template.name,
          config: {
            config: pageConfigData.config  // Double nesting as expected
          },
          configs: componentConfigs
        };

        const updated = await axios
          .post(`/odash/pages/${page_id}/configure`, payload)
          .then((e) => e.data)
          .catch((error) => {
            console.error("[PageTemplate] Error configuring page:", error);
            if (error.response) {
              console.error("[PageTemplate] Error response:", error.response.data);
              console.error("[PageTemplate] Error status:", error.response.status);
            }
            return null;
          });

        if (updated) {
          await loadPages();
          await loadConfigs();
          setTimeout(() => {
            navigate(`/${page_id}`);
            setOpen(false);
          }, 100);
        }
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" variant={"outline"}>
          <BookTemplate className="w-4 h-4 mr-2" />
          {t("home.pageTemplateDialog.usePageTemplate")}
        </Button>
      </DialogTrigger>
      <DialogContent className="!max-w-6xl">
        <DialogHeader>
          <DialogTitle>
            {t("home.pageTemplateDialog.chooseTemplate")}
          </DialogTitle>
          <DialogDescription>
            {t("home.pageTemplateDialog.templateDescription")}
          </DialogDescription>
        </DialogHeader>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t("home.pageTemplateDialog.searchPlaceholder") || "Search templates, categories, or tags..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <ScrollArea className="h-[50vh]">
          <div className="p-2 pr-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">{t("home.pageTemplateDialog.loadingTemplates")}</p>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery
                    ? t("home.pageTemplateDialog.noMatchingTemplates") || `No templates found matching "${searchQuery}"`
                    : t("home.pageTemplateDialog.noTemplatesAvailable")}
                </p>
                <p className="text-sm text-muted-foreground/70">
                  {searchQuery
                    ? t("home.pageTemplateDialog.tryDifferentSearch") || "Try a different search term"
                    : t("home.pageTemplateDialog.checkBackLater")}
                </p>
              </div>
            ) : (
              // All Templates Grouped by Category
              <div className="space-y-6">
                {filteredCategories.map((category) => (
                  <div key={category.id}>
                    {/* Category Header */}
                    <div className="flex items-center gap-3 mb-3 pb-2 border-b">
                      <div className={`p-2 rounded-lg ${category.color}`}>
                        {category.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{category.categoryName}</h3>
                        <p className="text-sm text-muted-foreground">{category.categoryDescription}</p>
                      </div>
                      <Badge variant="secondary">
                        {category.templates.length} {category.templates.length === 1 ? t("home.blockTemplateDialog.template") : t("home.blockTemplateDialog.templates")}
                      </Badge>
                    </div>

                    {/* Templates in this Category */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {category.templates.map((template: OdashTemplate) => {
                        const blockCounts = countBlocksByType(template);

                        return (
                          <Card
                            key={template.id}
                            className={`cursor-pointer transition-all hover:shadow-md ${
                              selectedTemplate === template.id
                                ? "ring-2 ring-primary border-primary"
                                : "hover:border-muted-foreground/50"
                            }`}
                            onClick={() => handleTemplateSelect(template.id)}
                          >
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                                    <FileText className="w-6 h-6" />
                                  </div>
                                  <div>
                                    <CardTitle className="text-base">
                                      {template.name}
                                    </CardTitle>
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                      {template.tags?.map((tag) => (
                                        <Badge
                                          key={tag.id}
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {tag.name}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                {selectedTemplate === template.id && (
                                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                    <div className="w-2 h-2 rounded-full bg-white" />
                                  </div>
                                )}
                              </div>
                            </CardHeader>
                            <CardContent>
                              <CardDescription className="text-sm mb-3">
                                {template.short_description || template.description || t("home.pageTemplateDialog.noDescription")}
                              </CardDescription>
                              <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
                                <div className="flex items-center gap-1">
                                  <BarChart3 className="w-3 h-3" />
                                  <span>
                                    {blockCounts.data}{" "}
                                    {t("home.pageTemplateDialog.dataBlocks")}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <TrendingUp className="w-3 h-3" />
                                  <span>
                                    {blockCounts.graph}{" "}
                                    {t("home.pageTemplateDialog.charts")}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Package className="w-3 h-3" />
                                  <span>
                                    {blockCounts.table}{" "}
                                    {t("home.pageTemplateDialog.tables")}
                                  </span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <Button variant="outline">
              {t("home.pageTemplateDialog.cancel")}
            </Button>
          </DialogClose>
          <Button disabled={!selectedTemplate} onClick={handleUseTemplate}>
            {t("home.pageTemplateDialog.useThisTemplate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PageTemplateDialog;
